import { LlmModel, TechnologyTrlProjectionSelectDto, TrlProjectionPointDto } from '@@types/api.types';
import { LONG_DESCRIPTION_MAX_LENGTH } from '@azymmetric/shared/validation/validation.constants';
import { IconButton } from '@components/core/icon-button.component';
import { ConfidenceScore } from '@components/shared/confidence-score.component';
import { PromptPopover } from '@components/shared/prompt-popover.component';
import { Score } from '@components/shared/score.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table';
import {
  Tooltip as UiTooltip,
  TooltipContent as UiTooltipContent,
  TooltipProvider as UiTooltipProvider,
  TooltipTrigger as UiTooltipTrigger,
} from '@components/ui/tooltip';
import { successToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import {
  useGenerateTechnologyTrlProjectionAnalysis,
  useGetTechnology,
  useGetTechnologyTrlProjection,
} from '@queries/technology.query';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { isStructuredOutputModelSelectable } from '@utils/llm-model.util';
import { Loader2Icon, SparklesIcon } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { TRL_PROJECTION_ANALYSIS_PROMPT_GUIDELINES } from '../../../../../../constants/prompts.constants';
import {
  TrlDensityPoint,
  TrlProjectionStep,
  buildLogNormalDensity,
  buildProjectionStepsFromTargets,
} from './trl-projection.utils';

type TrlProjectionContentProps = {
  technologyId: string;
};

type SeriesConfig = {
  color: string;
  key: 'p10' | 'p50' | 'p90';
  label: string;
};

type CumulativeTooltipProps = {
  active?: boolean;
  payload?: {
    color: string;
    dataKey: string;
    name: string;
    payload: TrlProjectionPointDto;
    value: number;
  }[];
};

type DensityTooltipProps = {
  active?: boolean;
  payload?: {
    color: string;
    dataKey: string;
    name: string;
    payload: TrlDensityPoint;
    value: number;
  }[];
};

type CumulativeProjectionTileProps = {
  projection: TechnologyTrlProjectionSelectDto;
};

type StepDurationTileProps = {
  step: TrlProjectionStep;
  stepIndex: number;
};

type StepMarker = {
  color: string;
  label: string;
  value: number;
  variant: 'destructive' | 'primary' | 'success';
};

type ProjectionTableTileProps = {
  isGenerating: boolean;
  onGenerateAnalysis: (data: { model?: LlmModel; prompt?: string }) => Promise<void>;
  projection: TechnologyTrlProjectionSelectDto;
  technologyName?: string;
};

type ProjectionTableRow = TechnologyTrlProjectionSelectDto['targets'][number];

const SERIES_CONFIG: SeriesConfig[] = [
  { color: '#EF4444', key: 'p90', label: 'P90 (Conservative)' },
  { color: '#3B82F6', key: 'p50', label: 'P50 (Expected)' },
  { color: '#22C55E', key: 'p10', label: 'P10 (Aggressive)' },
];

function formatMonths(value: number): string {
  return `${Math.round(value)}m`;
}

function getProjectionAnalysisDescription(technologyName?: string) {
  return (
    <>
      Use Tyr&apos;s AI assistant to analyze projection confidence and generate dominant-driver narratives for{' '}
      <b>{technologyName || 'this technology'}</b>. Timing remains deterministic. Requests to raise confidence are
      ignored; only concrete supporting evidence can affect the score.
    </>
  );
}

function buildAxisTicks(params: { max: number; min?: number }): number[] {
  const { max, min = 0 } = params;
  const range = Math.max(max - min, 0);
  const step = range <= 24 ? 2 : range <= 36 ? 3 : range <= 60 ? 5 : range <= 96 ? 8 : range <= 144 ? 10 : 12;

  const ticks: number[] = [];

  for (let value = min; value <= max; value += step) {
    ticks.push(Math.round(value));
  }

  return ticks;
}

const CumulativeTooltip: FC<CumulativeTooltipProps> = (props) => {
  const { active, payload } = props;

  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const data = payload[0].payload;

  return (
    <div className="flex flex-col gap-1 rounded border border-gray-200 bg-white p-2 shadow-sm">
      <div className="font-medium text-gray-700">Month {data.month}</div>
      {SERIES_CONFIG.map((series) => {
        const entry = payload.find((item) => item.dataKey === series.key);
        if (!entry) return null;

        return (
          <div
            className="flex items-center gap-2"
            key={series.key}
          >
            <div
              className="h-3 w-3 rounded-full"
              style={{ backgroundColor: series.color }}
            />
            <span className="text-sm text-gray-600">
              {series.label}: TRL {entry.value.toFixed(1)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const DensityTooltip: FC<DensityTooltipProps> = (props) => {
  const { active, payload } = props;

  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const data = payload[0].payload;

  return (
    <div className="flex flex-col gap-1 rounded border border-gray-200 bg-white p-2 shadow-sm">
      <div className="font-medium text-gray-700">Duration: {formatMonths(data.month)}</div>
      <div className="text-sm text-gray-600">Density: {data.density.toFixed(3)}</div>
    </div>
  );
};

const CumulativeProjectionTile: FC<CumulativeProjectionTileProps> = (props) => {
  const { projection } = props;
  const cumulativeTicks = useMemo(() => buildAxisTicks({ max: projection.maxMonths }), [projection.maxMonths]);

  return (
    <Tile
      header={{
        title: 'Cumulative TRL Progression Over Time',
      }}
    >
      <div className="flex flex-col gap-4">
        <div className="h-[320px] w-full min-w-0 overflow-hidden">
          <ResponsiveContainer
            height="100%"
            width="100%"
          >
            <AreaChart
              data={projection.points}
              margin={{ bottom: 12, left: 28, right: 30, top: 16 }}
            >
              <CartesianGrid
                stroke="#eee"
                strokeDasharray="3 3"
              />
              <XAxis
                dataKey="month"
                minTickGap={16}
                tick={{ fill: '#666' }}
                ticks={cumulativeTicks}
              />
              <YAxis
                domain={[1, 9]}
                label={{
                  angle: -90,
                  offset: 0,
                  position: 'insideLeft',
                  style: { fontSize: 12 },
                  textAnchor: 'middle',
                  value: 'TRL',
                }}
                ticks={[1, 2, 3, 4, 5, 6, 7, 8, 9]}
                tick={{ fill: '#666' }}
              />
              <Tooltip content={<CumulativeTooltip />} />
              <Legend />
              {SERIES_CONFIG.map((series) => (
                <Area
                  activeDot={{ r: 5 }}
                  dataKey={series.key}
                  dot={false}
                  fill={series.color}
                  fillOpacity={0.15}
                  key={series.key}
                  name={series.label}
                  stroke={series.color}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="text-center text-xs text-gray-500">Months from now</div>
      </div>
    </Tile>
  );
};

const ProjectionTableTile: FC<ProjectionTableTileProps> = (props) => {
  const { isGenerating, onGenerateAnalysis, projection, technologyName } = props;
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const hasAnalysisContent = projection.analysisStatus === 'fresh' || projection.analysisStatus === 'stale';
  const columns = useMemo<ColumnDef<ProjectionTableRow>[]>(
    () => [
      {
        accessorKey: 'targetTrl',
        minSize: 88,
        size: 88,
        cell: ({ row }) => (
          <Score
            score={row.original.targetTrl}
            size="small"
            type="TRL"
          />
        ),
        header: 'Target TRL',
      },
      {
        accessorKey: 'label',
        minSize: 220,
        size: 220,
        cell: ({ row }) => <span className="whitespace-nowrap text-sm text-gray-700">{row.original.label}</span>,
        header: 'Label',
      },
      {
        accessorKey: 'p10Months',
        minSize: 88,
        size: 88,
        cell: ({ row }) => <span className="text-sm text-gray-700">{formatMonths(row.original.p10Months)}</span>,
        header: 'P10',
      },
      {
        accessorKey: 'p50Months',
        minSize: 88,
        size: 88,
        cell: ({ row }) => <span className="text-sm text-gray-700">{formatMonths(row.original.p50Months)}</span>,
        header: 'P50',
      },
      {
        accessorKey: 'p90Months',
        minSize: 88,
        size: 88,
        cell: ({ row }) => <span className="text-sm text-gray-700">{formatMonths(row.original.p90Months)}</span>,
        header: 'P90',
      },
      {
        accessorKey: 'dominantDrivers',
        cell: ({ row }) => (
          <p className="text-sm leading-6 text-gray-700">
            {row.original.dominantDriversNarrative ?? row.original.deterministicDrivers.join('; ')}
          </p>
        ),
        header: 'Dominant Drivers',
      },
    ],
    [],
  );
  const table = useReactTable({
    columns,
    data: projection.targets,
    getCoreRowModel: getCoreRowModel(),
  });

  const generateAction = projection.canRegenerateAnalysis ? (
    <PromptPopover
      description={getProjectionAnalysisDescription(technologyName)}
      information={TRL_PROJECTION_ANALYSIS_PROMPT_GUIDELINES}
      isLoading={isGenerating}
      isOpen={isPromptOpen}
      maxLength={LONG_DESCRIPTION_MAX_LENGTH}
      modelFilter={isStructuredOutputModelSelectable}
      onOpenChange={setIsPromptOpen}
      onSubmit={onGenerateAnalysis}
      title={TRL_PROJECTION_ANALYSIS_PROMPT_GUIDELINES.title}
      trigger={
        hasAnalysisContent ? (
          <IconButton
            ariaLabel={isGenerating ? 'Analyzing projection' : 'Re-analyze projection'}
            className={cn(isGenerating && 'pointer-events-none')}
            icon={isGenerating ? <Loader2Icon className="animate-spin" /> : <SparklesIcon />}
            variant="secondary"
          />
        ) : (
          <Button
            className={cn('mx-auto w-fit', isGenerating && 'hover:no-underline')}
            disabled={isGenerating}
            variant="link"
          >
            {isGenerating ? 'Analyzing Projection...' : 'Analyze Projection'}
            {isGenerating ? <Loader2Icon className="size-4 animate-spin" /> : <SparklesIcon className="size-4" />}
          </Button>
        )
      }
    />
  ) : undefined;

  return (
    <Tile
      header={{
        actions: hasAnalysisContent ? generateAction : undefined,
        title: 'Projection Analysis',
      }}
      emptyState={{
        action: generateAction,
        title: 'Projection Analysis',
      }}
      isEmpty={!hasAnalysisContent}
    >
      <div className="mb-4 rounded-xl border p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <ConfidenceScore
              className="border"
              design="box"
              score={projection.confidenceScore ?? null}
              size="small"
            />
            <div className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-gray-700">Confidence</span>
              <span className="text-sm text-gray-700">
                {projection.confidenceAnalysis ?? 'Confidence analysis is not available.'}
              </span>
            </div>
          </div>
          {projection.analysisStatus === 'stale' ? (
            <UiTooltipProvider delayDuration={0}>
              <UiTooltip>
                <UiTooltipTrigger asChild>
                  <Badge variant="warning">Stale</Badge>
                </UiTooltipTrigger>
                <UiTooltipContent className="max-w-xs text-xs">
                  Some technology values were updated after this analysis was generated. Regenerating the projection is
                  recommended.
                </UiTooltipContent>
              </UiTooltip>
            </UiTooltipProvider>
          ) : null}
        </div>
        {projection.missingInputs.length > 0 ? (
          <div className="mt-3 flex flex-col gap-1 pl-[54px]">
            <span className="text-sm font-semibold text-gray-700">Next Steps</span>
            {projection.missingInputs.map((input) => (
              <span
                className="text-sm text-gray-600"
                key={input}
              >
                • {input}
              </span>
            ))}
          </div>
        ) : null}
      </div>
      <div className="overflow-hidden rounded-xl border">
        <Table className="w-full">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    className="whitespace-nowrap text-xs font-semibold text-gray-500"
                    key={header.id}
                    style={header.column.id === 'dominantDrivers' ? undefined : { width: header.getSize() }}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                className="align-middle"
                key={row.id}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    className={cn('py-4 align-middle', cell.column.id === 'dominantDrivers' && 'w-full min-w-[320px]')}
                    key={cell.id}
                    style={cell.column.id === 'dominantDrivers' ? undefined : { width: cell.column.getSize() }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Tile>
  );
};

const StepDurationTile: FC<StepDurationTileProps> = (props) => {
  const { step, stepIndex } = props;

  const densityData = useMemo(() => {
    return buildLogNormalDensity({
      p10Months: step.p10Months,
      p50Months: step.p50Months,
      p90Months: step.p90Months,
    });
  }, [step.p10Months, step.p50Months, step.p90Months]);

  const maxMonth = densityData[densityData.length - 1]?.month ?? 0;
  const axisTicks = useMemo(() => buildAxisTicks({ max: maxMonth }), [maxMonth]);

  const markers: StepMarker[] = [
    { color: '#15803D', label: 'P10', value: step.p10Months, variant: 'success' },
    { color: '#3B82F6', label: 'P50', value: step.p50Months, variant: 'primary' },
    { color: '#DC2626', label: 'P90', value: step.p90Months, variant: 'destructive' },
  ];

  return (
    <Tile
      header={{
        title: (
          <div className="flex items-center gap-3 justify-between w-full">
            <span>{`TRL ${step.fromTrl} -> ${step.toTrl} Duration Probability`}</span>
            <Badge
              size="sm"
              variant="outline"
            >
              Step {stepIndex + 1}
            </Badge>
          </div>
        ),
      }}
    >
      <div className="flex flex-col gap-3">
        <div className="h-[260px] w-full min-w-0 overflow-hidden">
          <ResponsiveContainer
            height="100%"
            width="100%"
          >
            <AreaChart
              data={densityData}
              margin={{ bottom: 20, left: 26, right: 30, top: 36 }}
            >
              <CartesianGrid
                stroke="#eee"
                strokeDasharray="3 3"
              />
              <XAxis
                dataKey="month"
                domain={[0, maxMonth]}
                interval={0}
                label={{ offset: -10, position: 'insideBottom', value: 'Duration (months)' }}
                tick={{ fill: '#666' }}
                ticks={axisTicks}
                type="number"
              />
              <YAxis
                label={{
                  angle: -90,
                  dy: 48,
                  offset: -8,
                  position: 'insideLeft',
                  style: { fontSize: 14 },
                  textAnchor: 'middle',
                  value: 'Probability Density',
                }}
                tick={{ fill: '#666' }}
              />
              <Tooltip content={<DensityTooltip />} />
              <Area
                activeDot={{ r: 5 }}
                dataKey="density"
                dot={false}
                fill="#60A5FA4D"
                name="Probability Density"
                stroke="#3B82F6"
                strokeWidth={2}
                type="monotone"
              />
              {markers.map((marker) => (
                <ReferenceLine
                  key={marker.label}
                  label={{
                    dy: -6,
                    fill: marker.color,
                    fontSize: 11,
                    fontWeight: 600,
                    position: 'top',
                    value: marker.label,
                  }}
                  stroke={marker.color}
                  strokeDasharray="4 4"
                  strokeOpacity={0.45}
                  strokeWidth={2}
                  x={marker.value}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-wrap gap-2 text-sm text-gray-700">
          <Badge
            className="border-transparent"
            size="sm"
            variant="success"
          >
            P10: {formatMonths(step.p10Months)}
          </Badge>
          <Badge
            className="border-transparent"
            size="sm"
            variant="primary"
          >
            P50: {formatMonths(step.p50Months)}
          </Badge>
          <Badge
            className="border-transparent"
            size="sm"
            variant="destructive"
          >
            P90: {formatMonths(step.p90Months)}
          </Badge>
        </div>
      </div>
    </Tile>
  );
};

export const TrlProjectionContent: FC<TrlProjectionContentProps> = (props) => {
  const { technologyId } = props;
  const { data: projection, isPending: isProjectionPending } = useGetTechnologyTrlProjection(technologyId);
  const { data: technology, isPending: isTechnologyPending } = useGetTechnology(technologyId);
  const { isPending: isGenerating, mutateAsync: generateProjectionAnalysis } =
    useGenerateTechnologyTrlProjectionAnalysis();

  const steps = useMemo(() => {
    if (!projection?.baseTrl || projection.targets.length === 0) {
      return [];
    }

    return buildProjectionStepsFromTargets({
      baseTrl: projection.baseTrl,
      targets: projection.targets,
    });
  }, [projection?.baseTrl, projection?.targets]);

  const handleGenerateAnalysis = async (data: { model?: LlmModel; prompt?: string }) => {
    await generateProjectionAnalysis({
      ...data,
      technologyId,
    });
    successToast({ description: 'Updated TRL projection analysis' });
  };

  if (isProjectionPending || isTechnologyPending) {
    return (
      <div className="flex flex-col gap-4">
        <Tile isLoading />
      </div>
    );
  }

  if (!projection || !projection.baseTrl || projection.targets.length === 0) {
    const message = projection?.baseTrl ? 'TRL 9 already achieved.' : 'Featured TRL assessment required.';

    return (
      <div className="flex flex-col gap-4">
        <Tile
          emptyState={{ message, title: 'TRL Projection Unavailable' }}
          isEmpty
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 min-w-0">
      <CumulativeProjectionTile projection={projection} />

      <ProjectionTableTile
        isGenerating={isGenerating}
        onGenerateAnalysis={handleGenerateAnalysis}
        projection={projection}
        technologyName={technology?.name}
      />

      <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
        {steps.map((step, index) => (
          <div
            className="min-w-0 w-full"
            key={`${step.fromTrl}-${step.toTrl}`}
          >
            <StepDurationTile
              step={step}
              stepIndex={index}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
